import { Box } from '@cast/design-system';
import { FailedToLoad } from '@cast/shared-messages-ui';
import { useDisabledMetricsBoxes } from '@cast/features-cluster-shared';
import {
  ChartWithPrediction,
  useGpuMetricsData,
  gpuMetricToColor,
  gpuMetricToTitle,
  gpuMetricToTooltipTitle,
  GpuMetricKey,
} from '@cast/split-me/features/cluster/workload-optimization';
import { InventoryResourceMetricBoxPair } from '../../../_components/ui/InventoryResourceMetricBoxPair';

import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { useInventoryWorkloadsTimeRangeSelection } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';

export const GpuMemoryChart = () => {
  const { timeZone, getRange, preset, key } =
    useInventoryWorkloadsTimeRangeSelection();
  const timeRangeSelection = {
    getRange,
    preset,
    timeZone,
    key,
  };
  const { normalized, isFetching, error, refetch, workload } =
    useGpuMetricsData({ timeRangeSelection });
  const { toggleMetric, isMetricDisabled } =
    useDisabledMetricsBoxes<GpuMetricKey>();

  const isLoading = isFetching || !workload || !normalized;

  if (error && !isFetching) {
    return (
      <WidgetLayout title="GPU memory" testId="gpu-memory-chart">
        <FailedToLoad title="Failed to load GPU memory" refresh={refetch} />
      </WidgetLayout>
    );
  }

  return (
    <WidgetLayout title="GPU memory" testId="gpu-memory-chart">
      <Box display="flex" flexDirection="column" gap={12} height="100%">
        {normalized && (
          <InventoryResourceMetricBoxPair
            testId="gpu-memory-metrics-box"
            currentDataKey="memoryUsedGB"
            currentValue={normalized.avgMemoryUsedGB}
            recommendedDataKey="recMemoryUsedGB"
            recommendedValue={normalized.avgRecMemoryUsedGB}
            unit="GiB"
            colorMap={gpuMetricToColor}
            titleMap={gpuMetricToTitle}
            toggleMetric={toggleMetric}
            isMetricDisabled={isMetricDisabled}
          />
        )}
        <Box flex={1}>
          <ChartWithPrediction
            data={normalized?.intervals ?? []}
            dataKeys={['memoryUsedGB', 'recMemoryUsedGB']}
            colorMap={gpuMetricToColor}
            tooltipTitleMap={gpuMetricToTooltipTitle}
            unit="GiB"
            analyzeDescription="GPU memory metric with prediction"
            timeZone={timeZone}
            isLoading={isLoading}
            isMetricDisabled={isMetricDisabled}
            hasError={!!error && !isFetching}
            refetch={refetch}
          />
        </Box>
      </Box>
    </WidgetLayout>
  );
};
